import React, { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, CircularProgress, Paper } from '@mui/material';
import { DomainTreeNode, DomainNodeData } from './DomainTreeNode';
import { fetchApi } from '@/api/fetchApi';

export interface DomainTreeProps {
  selected?: string;
  onSelect?: (domainId: string) => void;
  filterStatus?: string;
}

function filterTree(nodes: DomainNodeData[], status?: string): DomainNodeData[] {
  if (!status) return nodes;
  return nodes
    .filter((n) => n.status === status)
    .map((n) => ({
      ...n,
      children: n.children ? filterTree(n.children, status) : undefined,
    }));
}

function expandAll(nodes: DomainNodeData[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: DomainNodeData[]) => {
    for (const n of list) {
      if (n.children && n.children.length > 0) {
        ids.add(n.domainId);
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return ids;
}

export function DomainTree({
  selected,
  onSelect,
  filterStatus,
}: DomainTreeProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['domains', 'tree'],
    queryFn: () => fetchApi('/domains/tree'),
    staleTime: 5 * 60 * 1000,
  });

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (data) {
      setExpanded(expandAll(data as DomainNodeData[]));
    }
  }, [data]);

  const handleToggle = useCallback((domainId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(domainId)) next.delete(domainId);
      else next.add(domainId);
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 2 }}>
        Failed to load domain tree.
      </Typography>
    );
  }

  const domains = filterTree((data as DomainNodeData[]) || [], filterStatus);

  if (domains.length === 0) {
    return (
      <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No domains available.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {domains.map((domain) => (
        <DomainTreeNode
          key={domain.domainId}
          domain={domain}
          depth={0}
          selected={selected}
          onSelect={onSelect}
          expanded={expanded}
          onToggle={handleToggle}
        />
      ))}
    </Box>
  );
}
